import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fileUrl, uploadFile, asItems, asTotal, API } from "@/lib/api";
import { money, dt, ORDER_STATES, ORDER_LABELS, ORDER_COLORS, CHANNEL_LABELS, PAYMENT_LINK_LABELS, PAYMENT_LINK_COLORS } from "@/lib/format";
import { PageHeader, Loader, EmptyState, Pagination } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingBag, Copy, Truck, CreditCard, Printer, HandCoins, AlertTriangle, Link2, Send, Ban, Download, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState(null);
  const [fEstado, setFEstado] = useState("todos");
  const [fCanal, setFCanal] = useState("todos");
  const [fQuery, setFQuery] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 50;
  const [detail, setDetail] = useState(null);
  const [pago, setPago] = useState({ tipo: "transferencia", monto: 0, comprobante_url: null, comentario: "" });
  const [showPago, setShowPago] = useState(false);
  const [cobro, setCobro] = useState({ monto_cobrado: 0, medio: "efectivo", mensajero: "", observaciones: "" });
  const [showCobro, setShowCobro] = useState(false);
  const [links, setLinks] = useState([]);
  const [creatingLink, setCreatingLink] = useState(false);
  const [zonasEnvio, setZonasEnvio] = useState([]); // Fix K
  const debounceRef = useRef(null);
  useEffect(() => {
    api.get("/settings").then((r) => setZonasEnvio((r.data?.zonas_envio_lista || []).filter((z) => z.activa !== false))).catch(() => {});
  }, []);

  const filterParams = () => {
    const params = {};
    if (fEstado !== "todos") params.estado = fEstado;
    if (fCanal !== "todos") params.canal = fCanal;
    if (qDebounced.trim()) params.q = qDebounced.trim();
    return params;
  };

  const load = async () => {
    const params = { ...filterParams(), skip, limit: LIMIT };
    const r = await api.get("/orders", { params });
    setOrders(asItems(r));
    setTotal(asTotal(r));
  };
  useEffect(() => { setSkip(0); /* eslint-disable-next-line */ }, [fEstado, fCanal, qDebounced]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fEstado, fCanal, qDebounced, skip]);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQDebounced(fQuery), 350);
    return () => clearTimeout(debounceRef.current);
  }, [fQuery]);

  const exportCsv = () => {
    const token = localStorage.getItem("ms_token");
    const params = new URLSearchParams(filterParams());
    window.open(`${API}/orders/export?${params.toString()}&auth=${token}`, "_blank");
  };

  const openDetail = async (o) => {
    const r = await api.get(`/orders/${o.id}`);
    setDetail(r.data);
    const rl = await api.get(`/orders/${o.id}/links-pago`);
    setLinks(rl.data);
  };

  useEffect(() => {
    const abrir = searchParams.get("abrir");
    if (abrir) openDetail({ id: abrir });
    // eslint-disable-next-line
  }, []);

  const isContraEntrega = (o) => ["efectivo_contra_entrega", "contra_entrega"].includes(o?.metodo_pago);
  // Fix K (2026-09-22): cambiar la zona recalcula el envío con la misma regla que usa el agente.
  const motivoEnvioBloqueado = (o) => {
    if (!o) return "";
    if (o.pago_validado) return "el pago ya está validado";
    if (["despachado", "entregado", "cancelado"].includes(o.estado)) return `el pedido está ${(ORDER_LABELS[o.estado] || o.estado).toLowerCase()}`;
    if (links.some((l) => l.estado === "pendiente")) return "hay un link de pago pendiente; hay que anularlo primero";
    return "";
  };
  const cambiarZonaEnvio = async (zona) => {
    try {
      const { data } = await api.put(`/orders/${detail.id}`, { zona_envio: zona });
      setDetail({ ...detail, ...data });
      toast.success(`Envío recalculado: ${money(data.costo_envio || 0)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo recalcular el envío"); }
  };

  const changeEstado = async (estado) => {
    if (estado === "entregado" && isContraEntrega(detail) && !detail.pago_validado) {
      setCobro({ monto_cobrado: detail.total, medio: "efectivo", mensajero: detail.mensajero || "", observaciones: "" });
      setShowCobro(true);
      return;
    }
    try { const r = await api.post(`/orders/${detail.id}/estado`, { estado }); setDetail({ ...detail, ...r.data }); toast.success("Estado actualizado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const registrarCobroEntrega = async () => {
    if (!cobro.mensajero.trim()) return toast.error("Indicá el mensajero");
    try {
      const { data } = await api.post(`/orders/${detail.id}/entregar-cobro`, { ...cobro, monto_cobrado: Number(cobro.monto_cobrado) });
      toast.success("Cobro registrado, pedido entregado");
      setShowCobro(false);
      openDetail(data.pedido); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const setMensajero = async (mensajero) => {
    await api.put(`/orders/${detail.id}`, { mensajero });
    setDetail({ ...detail, mensajero });
  };

  const onComprobante = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const { path } = await uploadFile(f); setPago((p) => ({ ...p, comprobante_url: path })); toast.success("Comprobante subido"); }
    catch { toast.error("No se pudo subir"); }
  };

  const registrarPago = async () => {
    try {
      const { data: pagoCreado } = await api.post(`/orders/${detail.id}/pago`, { ...pago, monto: Number(pago.monto) || detail.total });
      toast.success(pago.tipo === "efectivo" ? "Pago registrado"
        : pagoCreado?.estado === "validado" ? "Comprobante verificado: pago validado automáticamente ✓" : "Comprobante enviado a validación");
      setShowPago(false); setPago({ tipo: "transferencia", monto: 0, comprobante_url: null, comentario: "" });
      openDetail(detail); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const generarLink = async () => {
    setCreatingLink(true);
    try {
      await api.post(`/orders/${detail.id}/links-pago`, {});
      toast.success("Link de pago generado");
      openDetail(detail);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setCreatingLink(false); }
  };
  const reenviarLink = async (l) => {
    try { const { data } = await api.post(`/links-pago/${l.id}/reenviar`); toast.success(data.enviado_por_chat ? "Link reenviado por chat" : "No hay chat activo, copiá el link manualmente"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const anularLink = async (l) => {
    try { await api.post(`/links-pago/${l.id}/anular`); toast.success("Link anulado"); openDetail(detail); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const copiarLink = (l) => { navigator.clipboard.writeText(l.url_pago); toast.success("URL copiada"); };

  const copyResumen = (o) => {
    const lines = [
      `*Pedido #${o.numero}* — Marthitashop`,
      `Cliente: ${o.cliente_nombre || "-"}`,
      ...o.items.map((i) => `• ${i.cantidad} x ${i.nombre} — ${money(i.precio_unitario * i.cantidad)}`),
      `Subtotal: ${money(o.subtotal)}`,
      o.descuento ? `Descuento: -${money(o.descuento)}` : null,
      o.costo_envio ? `Envío: ${money(o.costo_envio)}` : null,
      `*Total: ${money(o.total)}*`,
      o.direccion_entrega ? `Entrega: ${o.direccion_entrega}` : null,
      `Pago: ${o.metodo_pago}`,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(lines);
    toast.success("Resumen copiado para pegar en WhatsApp");
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto">
      <PageHeader title="Pedidos" subtitle="Gestioná estados, pagos y entregas.">
        <Button variant="outline" onClick={exportCsv} className="rounded-full gap-2" data-testid="export-orders-csv"><Download className="h-4 w-4" /> Exportar CSV</Button>
      </PageHeader>
      <div className="ms-card p-3 mb-4 flex flex-wrap gap-3">
        <div className="relative w-56">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={fQuery} onChange={(e) => setFQuery(e.target.value)} placeholder="Buscar # pedido, cliente o teléfono…"
            className="rounded-xl pl-9 pr-8" data-testid="search-orders" />
          {fQuery && (
            <button onClick={() => setFQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="clear-search-orders">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={fEstado} onValueChange={setFEstado}>
          <SelectTrigger className="w-44 rounded-xl" data-testid="filter-order-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {ORDER_STATES.map((s) => <SelectItem key={s} value={s}>{ORDER_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCanal} onValueChange={setFCanal}>
          <SelectTrigger className="w-40 rounded-xl" data-testid="filter-order-canal"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los canales</SelectItem>
            {Object.entries(CHANNEL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!orders ? <Loader /> : orders.length === 0 ? (
        qDebounced ? <EmptyState icon={ShoppingBag} title="Sin resultados" subtitle={`No encontramos pedidos para «${qDebounced}».`} />
          : <EmptyState icon={ShoppingBag} title="Sin pedidos" subtitle="No hay pedidos con esos filtros." />
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="ms-card ms-lift p-4 flex flex-wrap items-center gap-3 cursor-pointer" onClick={() => openDetail(o)}>
              <div className="flex-1 min-w-[160px]">
                <div className="flex items-center gap-2">
                  <span className="font-head font-semibold">#{o.numero}</span>
                  <Badge className={`rounded-full border-0 ${ORDER_COLORS[o.estado]}`}>{ORDER_LABELS[o.estado]}</Badge>
                  <span className="text-xs text-muted-foreground">{CHANNEL_LABELS[o.canal] || o.canal}</span>
                  {o.requiere_revision && (
                    <Badge className="rounded-full border-0 bg-amber-50 text-amber-600 gap-1" data-testid={`order-requiere-revision-${o.id}`}>
                      <AlertTriangle className="h-3 w-3" /> Requiere revisión
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{o.cliente_nombre || "Sin cliente"} · {o.items.length} artículos</p>
              </div>
              <span className="font-head font-semibold text-primary">{money(o.total)}</span>
              <Button size="sm" variant="outline" className="rounded-full" onClick={(e) => { e.stopPropagation(); copyResumen(o); }} data-testid={`copy-${o.id}`}><Copy className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <Pagination skip={skip} limit={LIMIT} total={total} onChange={setSkip} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="w-[min(680px,95vw)] max-w-[min(680px,95vw)] max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="order-detail-modal">
          {detail && (
            <>
              <DialogHeader className="shrink-0 border-b border-border px-5 py-4">
                <DialogTitle className="flex items-center gap-2 flex-wrap pr-6">Pedido #{detail.numero}
                <Badge className={`rounded-full border-0 ${ORDER_COLORS[detail.estado]}`}>{ORDER_LABELS[detail.estado]}</Badge></DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {detail.requiere_revision && (
                  <div className="rounded-2xl bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid="order-detail-requiere-revision">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-700 font-medium">Este pedido de Shopify tiene productos sin vincular a nuestro catálogo:</p>
                      <ul className="text-xs text-amber-700 mt-1 list-disc pl-4">
                        {(detail.items_no_mapeados || []).map((i, idx) => (
                          <li key={idx}>{i.cantidad} x {i.titulo} {i.sku ? `(SKU ${i.sku})` : ""}</li>
                        ))}
                      </ul>
                      <p className="text-xs text-amber-600 mt-1">Vinculá el producto en Shopify → Sincronización y volvé a intentar.</p>
                    </div>
                  </div>
                )}
                <div className="rounded-2xl border border-border p-3">
                  <p className="text-sm font-semibold">{detail.cliente_nombre}</p>
                  <p className="text-xs text-muted-foreground">{detail.cliente_telefono}</p>
                  <p className="text-xs text-muted-foreground mt-1">{detail.direccion_entrega}</p>
                </div>
                <div className="space-y-1">
                  {detail.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between text-sm gap-2">
                      <span className="min-w-0">{i.cantidad} x {i.nombre}</span><span className="shrink-0">{money(i.precio_unitario * i.cantidad)}</span>
                    </div>
                  ))}
                  <div className="border-t border-border pt-2 mt-2 flex justify-between text-sm"><span>Subtotal</span><span>{money(detail.subtotal)}</span></div>
                  {detail.descuento > 0 && <div className="flex justify-between text-sm text-emerald-600"><span>Descuento {detail.promo_aplicada ? `(${detail.promo_aplicada})` : ""}</span><span>-{money(detail.descuento)}</span></div>}
                  {(detail.costo_envio > 0 || detail.zona_envio) && <div className="flex justify-between text-sm"><span>Envío {detail.zona_envio ? `(${detail.zona_envio})` : ""}</span><span>{detail.costo_envio > 0 ? money(detail.costo_envio) : "Gratis"}</span></div>}
                  <div className="flex justify-between font-head font-semibold"><span>Total</span><span className="text-primary">{money(detail.total)}</span></div>
                </div>
                {zonasEnvio.length > 0 && (
                  <div className="rounded-2xl border border-border p-3 space-y-2" data-testid="order-envio-box">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-sm font-medium flex items-center gap-1"><Truck className="h-4 w-4 text-primary" /> Zona de envío</span>
                      <span className="text-xs text-muted-foreground">{detail.direccion_entrega}</span>
                    </div>
                    {motivoEnvioBloqueado(detail) ? (
                      <p className="text-xs text-muted-foreground" data-testid="order-envio-bloqueado">{detail.zona_envio || "Sin zona"} · No se puede cambiar: {motivoEnvioBloqueado(detail)}.</p>
                    ) : (
                      <Select value={zonasEnvio.some((z) => z.zona === detail.zona_envio) ? detail.zona_envio : ""} onValueChange={cambiarZonaEnvio}>
                        <SelectTrigger className="rounded-xl" data-testid="order-zona-select"><SelectValue placeholder="Elegir zona para calcular el envío" /></SelectTrigger>
                        <SelectContent>
                          {zonasEnvio.map((z) => <SelectItem key={z.id || z.zona} value={z.zona}>{z.zona} · {money(z.tarifa)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                <div>
                  <Label>Cambiar estado</Label>
                  <Select value={detail.estado} onValueChange={changeEstado}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="order-estado-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{ORDER_STATES.map((s) => <SelectItem key={s} value={s}>{ORDER_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input placeholder="Mensajero" defaultValue={detail.mensajero} onBlur={(e) => setMensajero(e.target.value)} className="rounded-xl" data-testid="order-mensajero" />
                </div>

                <div className="flex items-center justify-between rounded-2xl bg-accent/50 px-3 py-2 gap-2 flex-wrap">
                  <span className="text-sm">Pago {detail.pago_validado ? "validado ✓" : "pendiente"}</span>
                  <Button size="sm" onClick={() => { setPago((p) => ({ ...p, monto: detail.total })); setShowPago(true); }} className="rounded-full bg-primary hover:bg-primary/90 gap-1" data-testid="order-registrar-pago"><CreditCard className="h-4 w-4" /> Registrar pago</Button>
                </div>

                <div className="rounded-2xl border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-sm font-medium flex items-center gap-1"><Link2 className="h-4 w-4 text-primary" /> Link de pago</span>
                    {!links.some((l) => l.estado === "pendiente") && !detail.pago_validado && (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={generarLink} disabled={creatingLink} data-testid="order-generar-link">
                        {creatingLink ? "Generando…" : "Generar link"}
                      </Button>
                    )}
                  </div>
                  {links.length === 0 ? <p className="text-xs text-muted-foreground">Sin links generados para este pedido.</p> : (
                    <div className="space-y-1">
                      {links.map((l) => (
                        <div key={l.id} className="flex items-center justify-between gap-2 rounded-xl bg-accent/40 px-2 py-1.5" data-testid={`payment-link-${l.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <Badge className={`rounded-full border-0 ${PAYMENT_LINK_COLORS[l.estado]}`}>{PAYMENT_LINK_LABELS[l.estado]}</Badge>
                              <span className="text-xs text-muted-foreground">{money(l.monto)}</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground truncate max-w-full">{l.url_pago}</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="ghost" className="rounded-full h-7 w-7 p-0" onClick={() => copiarLink(l)} data-testid={`copiar-link-${l.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                            {l.estado === "pendiente" && (
                              <>
                                <Button size="sm" variant="ghost" className="rounded-full h-7 w-7 p-0" onClick={() => reenviarLink(l)} data-testid={`reenviar-link-${l.id}`}><Send className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="ghost" className="rounded-full h-7 w-7 p-0 text-red-500" onClick={() => anularLink(l)} data-testid={`anular-link-${l.id}`}><Ban className="h-3.5 w-3.5" /></Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {detail.pagos?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Pagos</p>
                    {detail.pagos.map((p) => (
                      <div key={p.id} className="flex justify-between text-sm rounded-xl border border-border px-3 py-2 gap-2">
                        <span className="capitalize min-w-0">{p.tipo} · {money(p.monto)}</span>
                        <Badge className={`rounded-full border-0 shrink-0 ${p.estado === "validado" ? "bg-emerald-50 text-emerald-600" : p.estado === "rechazado" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>{p.estado.replace("_", " ")}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="shrink-0 border-t border-border px-5 py-3 flex flex-col sm:flex-row gap-2">
                <Button variant="outline" className="w-full rounded-full gap-2" onClick={() => copyResumen(detail)} data-testid="order-copy-resumen"><Copy className="h-4 w-4" /> Copiar resumen para WhatsApp</Button>
                <Button variant="outline" className="w-full rounded-full gap-2" onClick={() => window.open(`/imprimir/pedido/${detail.id}`, "_blank")} data-testid="order-print"><Printer className="h-4 w-4" /> Imprimir pedido</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showCobro} onOpenChange={setShowCobro}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><HandCoins className="h-5 w-5 text-primary" /> Registrar cobro de entrega</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Monto cobrado</Label><Input type="number" value={cobro.monto_cobrado} onChange={(e) => setCobro({ ...cobro, monto_cobrado: e.target.value })} className="rounded-xl mt-1" data-testid="cobro-monto" /></div>
            <div>
              <Label>Medio de cobro</Label>
              <Select value={cobro.medio} onValueChange={(v) => setCobro({ ...cobro, medio: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="cobro-medio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia_al_momento">Transferencia al momento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Mensajero</Label><Input value={cobro.mensajero} onChange={(e) => setCobro({ ...cobro, mensajero: e.target.value })} className="rounded-xl mt-1" data-testid="cobro-mensajero" /></div>
            <div><Label>Observaciones</Label><Textarea value={cobro.observaciones} onChange={(e) => setCobro({ ...cobro, observaciones: e.target.value })} className="rounded-xl mt-1" /></div>
          </div>
          <Button onClick={registrarCobroEntrega} className="rounded-full bg-primary hover:bg-primary/90 w-full" data-testid="cobro-guardar">Confirmar entrega y cobro</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showPago} onOpenChange={setShowPago}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Registrar pago</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de pago</Label>
              <Select value={pago.tipo} onValueChange={(v) => setPago({ ...pago, tipo: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="pago-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="link_pago">Link de pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Monto</Label><Input type="number" value={pago.monto} onChange={(e) => setPago({ ...pago, monto: e.target.value })} className="rounded-xl mt-1" /></div>
            {pago.tipo === "transferencia" && (
              <div>
                <Label>Comprobante (foto)</Label>
                <Input type="file" accept="image/*" onChange={onComprobante} className="rounded-xl mt-1" data-testid="pago-comprobante" />
                {pago.comprobante_url && <img src={fileUrl(pago.comprobante_url)} alt="" className="mt-2 h-24 rounded-xl object-cover" />}
              </div>
            )}
          </div>
          <Button onClick={registrarPago} className="rounded-full bg-primary hover:bg-primary/90 w-full" data-testid="pago-guardar">Guardar pago</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
